import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { mailboxService, mailboxErrorMessage } from '../../services/mailbox.service';
import { canUseCrm } from '../../utils/crm';
import { getActiveRole } from '../../utils/getActiveRole';

const activeRole = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? getActiveRole(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
};

/**
 * Connect your Gmail account so Rialty can send email from your own address. Used inside Connected Services.
 *
 * It shows nothing at all unless the feature is switched on for this environment: if the status request fails for any
 * reason (the service answers 404 when it is off) the card stays hidden, so a deployment without it changes nothing.
 * Rialty asks Google only for permission to send; it never reads mail. Say so here, before the user is sent to Google.
 */
const GmailConnectionCard = () => {
  const [status, setStatus] = useState(null);          // null until known
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const eligible = canUseCrm(activeRole());

  const load = useCallback(async () => {
    try {
      setStatus(await mailboxService.getStatus());
      setAvailable(true);
    } catch {
      setAvailable(false);
    }
  }, []);

  useEffect(() => {
    if (eligible) load();
  }, [eligible, load]);

  const connect = async () => {
    setBusy(true);
    setError('');
    try {
      window.location.href = await mailboxService.getConnectUrl('google');
    } catch (err) {
      setError(mailboxErrorMessage(err));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError('');
    try {
      await mailboxService.disconnect();
      setConfirming(false);
      await load();
    } catch (err) {
      setError(mailboxErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!eligible || !available || !status) return null;

  const connected = status.connected;
  const needsReconnect = status.needsReconnect;

  return (
    <div className="cs-service-card" data-testid="gmail-card">
      <div className="cs-service-card-header">
        <div className="cs-service-card-info">
          <h2 className="cs-service-name">Gmail</h2>
          <p className="cs-service-desc">Send email to your clients from your own Gmail address</p>
        </div>
        <div className={`cs-status-badge ${connected ? 'cs-status-connected' : 'cs-status-disconnected'}`}>
          {connected ? 'Connected' : needsReconnect ? 'Reconnect needed' : 'Not connected'}
        </div>
      </div>

      <div className="cs-card-body">
        {connected && (
          <p>Connected as <strong>{status.address}</strong>. Emails you send from Rialty go out from this address and appear in your Gmail Sent folder.</p>
        )}
        {needsReconnect && (
          <p>Your Gmail connection has stopped working{status.address ? ` (${status.address})` : ''}. Reconnect to send from your own address again. Until then, emails are sent from Rialty on your behalf.</p>
        )}
        {!connected && !needsReconnect && (
          <>
            <p>
              Connect your Gmail account so the emails you send from Rialty, such as follow-ups to your contacts and offer packages, come from your
              own address and appear in your Gmail Sent folder.
            </p>
            <p className="cs-not-connected-text">
              Rialty will ask Google only for permission to <strong>send</strong> email on your behalf. We never read, search, or delete your
              messages, and we only send what you ask us to. You can disconnect at any time here or in your Google Account.{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">How we use Google data</Link>
            </p>
          </>
        )}

        {error && <p className="cs-error" role="alert">{error}</p>}

        <div className="cs-card-actions">
          {!connected && (
            <button type="button" className="cs-btn cs-btn-primary cs-btn-connect" onClick={connect} disabled={busy}>
              {busy ? 'Opening Google…' : needsReconnect ? 'Reconnect Gmail' : 'Connect Gmail'}
            </button>
          )}
          {(connected || needsReconnect) && !confirming && (
            <button type="button" className="cs-btn cs-btn-disconnect" onClick={() => setConfirming(true)} disabled={busy}>Disconnect</button>
          )}
          {confirming && (
            <>
              <span>Disconnect Gmail? Emails will be sent from Rialty again.</span>
              <button type="button" className="cs-btn cs-btn-disconnect" onClick={disconnect} disabled={busy}>{busy ? 'Disconnecting…' : 'Yes, disconnect'}</button>
              <button type="button" className="cs-btn cs-btn-secondary" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GmailConnectionCard;
